import { Box, IconButton, Button, Paper, InputLabel } from '@mui/material';
import { UseFormReturn, useFieldArray, SubmitHandler } from 'react-hook-form';
import { DragDropContext, Droppable, DropResult, Draggable } from 'react-beautiful-dnd';
import { getGeocodedValues } from 'utils/helpers';
import { GoogleConf, OfferLocation } from 'utils/data/data-types';
import { useTranslation } from 'react-i18next';
import {
  CorporateCharterItinerarySchemaType,
  MINIMUM_STOPOVERS,
} from 'components/forms/CorporateCharter/types';
import { DragIndicator, Close } from '@mui/icons-material';
import {
  colors,
  ControlledField,
  LocationInputField,
  PassengerCountField,
  stopover,
} from 'remote/global';
import TimePickerField from './FormFields/TimePickerField';

interface Props {
  googleConf: GoogleConf;
  selectedRow: number;
  setSelectedRow: (row: number) => void;
  onValid: SubmitHandler<CorporateCharterItinerarySchemaType>;
  formInstance: UseFormReturn<CorporateCharterItinerarySchemaType>;
}

interface LocationFieldProps {
  formInstance: UseFormReturn<CorporateCharterItinerarySchemaType>;
  googleConf: GoogleConf;
  selectedRow: number;
  index: number;
  translationKey?: string;
  noOptionsText?: string;
  setSelectedRow: (row: number) => void;
}
function LocationField({
  googleConf,
  formInstance,
  selectedRow,
  setSelectedRow,
  index,
  translationKey,
  noOptionsText,
}: LocationFieldProps) {
  const { control, setValue, clearErrors } = formInstance;
  return (
    <ControlledField
      control={control}
      name={`stops.${index}`}
      render={(renderProps) => (
        <LocationInputField
          {...renderProps}
          translationKey={translationKey || ''}
          focused={selectedRow === index}
          onChange={async (val) => {
            clearErrors(`stops.${index}`);

            const geocodedValues = await getGeocodedValues(val);
            if (!geocodedValues) return;

            const payload: OfferLocation = {
              address: val?.address,
              locationText: geocodedValues.locationText,
              placeId: geocodedValues.placeId,
              coords: {
                lat: geocodedValues.coords?.lat,
                lon: geocodedValues.coords?.lon,
              },
            };

            setValue(`stops.${index}`, {
              ...val,
              ...payload,
            });
          }}
          fieldProps={{
            disableClearable: true,
            id: `stops.${index}`,
            onFocus: () => setSelectedRow(index),
            noOptionsText,
          }}
          googleConfig={googleConf}
        />
      )}
    />
  );
}

const FIRST_DRAGGABLE_INDEX = 2;
function StopoverForm({ selectedRow, setSelectedRow, googleConf, onValid, formInstance }: Props) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { isSubmitting },
  } = formInstance;
  const { fields, remove, append } = useFieldArray({
    name: 'stops',
    keyName: 'fieldId',
    control,
  });

  const handleOnDragEnd = async ({ destination, source }: DropResult) => {
    if (!destination || destination.index === source.index) {
      return;
    }
    const items = getValues('stops');
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    setValue('stops', items);
  };
  const handleAddStopover = () => {
    setSelectedRow(fields.length);
    append(stopover, { focusName: `stops.${fields.length}` });
  };

  const legsLength = watch('stops').length;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Box alignItems='baseline' sx={{ borderRadius: '8px', zIndex: 99 }}>
        <Paper
          elevation={3}
          sx={{
            flexDirection: 'column',
            display: 'flex',
            p: '30px 25px',
            overflow: 'visible!important',
            mb: '12px',
          }}
        >
          <Box
            sx={{
              pl: {
                xs: '24px',
                md: '48px',
              },
              pr: { xs: '24px', md: '48px' },
              mb: '12px',
              width: '100%',
            }}
          >
            <LocationField
              formInstance={formInstance}
              googleConf={googleConf}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              index={0}
              translationKey='startDepotAddress'
              noOptionsText={t('MultiStop.labels.addPickupDepotLocation').toString()}
            />
            <Box onFocus={() => setSelectedRow(-1)}>
              <PassengerCountField form={formInstance} />
            </Box>
          </Box>
        </Paper>
        <Paper
          component={Paper}
          elevation={3}
          sx={{
            overflow: 'auto',
            height: '412px',
            padding: '30px 25px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              pl: {
                xs: '24px',
                md: '48px',
              },
              pr: { xs: '24px', md: '48px' },
              mb: '12px',
              width: '100%',
            }}
          >
            <LocationField
              formInstance={formInstance}
              googleConf={googleConf}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              index={1}
              translationKey='firstStop'
              noOptionsText={t('MultiStop.labels.chooseFromMap').toString()}
            />
            <Box onFocus={() => setSelectedRow(-1)}>
              <TimePickerField
                formInstance={formInstance}
                name='departures.0.clientDepartureTime'
              />
            </Box>
          </Box>
          <Box
            sx={{ pl: { xs: '24px', md: '48px' }, pr: { xs: '24px', md: '48px' }, width: '100%' }}
          >
            <InputLabel
              sx={{
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 500,
              }}
              htmlFor='stops.2'
              aria-label='stops.2'
              shrink
            >
              {t('MultiStop.labels.stopovers')}
            </InputLabel>
          </Box>
          <Box
            overflow='visible'
            sx={{
              pl: {
                xs: 0,
                md: '24px',
              },
              pr: {
                xs: '24px',
                md: '0',
              },
              mb: '12px',
              width: '100%',
            }}
          >
            <form id='stopovers-container'>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId='items'>
                  {(droppableProvided) => (
                    <Box {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                      {fields.map((field, index) => {
                        return (
                          index >= FIRST_DRAGGABLE_INDEX && (
                            <Draggable
                              key={field.fieldId}
                              draggableId={field.fieldId}
                              index={index}
                            >
                              {(provided) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: '12px',
                                    '.MuiAutocomplete-root': {
                                      width: '100%',
                                    },
                                  }}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  onClick={() => setSelectedRow(index)}
                                >
                                  <IconButton
                                    {...provided.dragHandleProps}
                                    sx={{
                                      padding: 0,
                                      color: colors.uiBlue600,
                                      ml: '-12px',
                                      mr: '12px',
                                    }}
                                  >
                                    <DragIndicator />
                                  </IconButton>
                                  <LocationField
                                    formInstance={formInstance}
                                    googleConf={googleConf}
                                    selectedRow={selectedRow}
                                    setSelectedRow={setSelectedRow}
                                    index={index}
                                    noOptionsText={t('MultiStop.labels.chooseFromMap').toString()}
                                  />
                                  <Box
                                    sx={{
                                      cursor:
                                        legsLength > MINIMUM_STOPOVERS ? 'auto' : 'not-allowed',
                                    }}
                                  >
                                    <IconButton
                                      disabled={legsLength <= MINIMUM_STOPOVERS}
                                      size='large'
                                      aria-label='remove row button'
                                      sx={{
                                        padding: 0,
                                        ml: '24px',
                                        color: colors.uiBlue600,
                                      }}
                                      onClick={() => remove(index)}
                                    >
                                      <Close />
                                    </IconButton>
                                  </Box>
                                </Box>
                              )}
                            </Draggable>
                          )
                        );
                      })}
                      {droppableProvided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </form>
          </Box>
        </Paper>
        <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
          <Button
            sx={{ pt: '30px' }}
            variant='text'
            color='primary'
            disableElevation
            id='add-stopover-button'
            onClick={handleAddStopover}
          >
            {t('MultiStop.labels.addStop')}
          </Button>
          <Button sx={{ mt: '24px' }} disabled={isSubmitting} type='submit'>
            {t('continue')}
          </Button>
        </Box>
      </Box>
    </form>
  );
}

export default StopoverForm;
